import type AnthropicSDK from '@anthropic-ai/sdk';
import type { infer as zInfer, ZodType } from 'zod';
import { L } from '@/i18n';

/**
 * Optional connection to Anthropic's Claude API, using the learner's (or a
 * parent's/teacher's) own API key, through the official SDK.
 *
 *  - The key is stored only in this browser's localStorage under its own entry.
 *    It is never part of Studio's state, backups or exports.
 *  - Requests go directly from the browser to api.anthropic.com (the SDK's
 *    `dangerouslyAllowBrowser` mode — acceptable here because the key belongs
 *    to the person using this browser, not to Studio). Studio's own server
 *    (accounts and sync) never sees the key or the AI traffic.
 *  - Everything produced this way is labelled as AI output in the UI.
 */

const KEY_STORAGE = 'studio.anthropic.key';

export const AI_MODELS: { id: string; label: string }[] = [
  { id: 'claude-opus-5-5', label: 'Claude Opus 5.5' },
  { id: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5' },
  { id: 'claude-haiku-5-5', label: 'Claude Haiku 5.5' },
];

export function getApiKey(): string {
  try {
    return localStorage.getItem(KEY_STORAGE) ?? '';
  } catch {
    return '';
  }
}

export function setApiKey(key: string) {
  try {
    if (key) localStorage.setItem(KEY_STORAGE, key.trim());
    else localStorage.removeItem(KEY_STORAGE);
  } catch {
    /* storage unavailable */
  }
}

export const hasApiKey = () => getApiKey().length > 10;

export interface AskOptions {
  model: string;
  system: string;
  prompt: string;
  maxTokens?: number;
  signal?: AbortSignal;
  /** Called with each text fragment as it streams in. */
  onText?: (fragment: string, full: string) => void;
}

export class AIError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
  }
}

/**
 * Opt in to Anthropic's server-side refusal fallback: if a model's safety
 * classifier declines (false positives happen), the API re-runs the request on
 * a recommended fallback model within the same call. Not available for Haiku.
 */
const supportsFallback = (model: string) => !model.startsWith('claude-haiku');

/** Stream a single-turn request. Resolves with the full text. */
export function ask(o: AskOptions): Promise<string> {
  return chat({ ...o, messages: [{ role: 'user', content: o.prompt }] });
}

/** The study chat runs on Claude Haiku 5.5: fast, and the cheapest for the learner's own key. */
export const CHAT_MODEL = 'claude-haiku-5-5';

export interface ChatOptions extends Omit<AskOptions, 'prompt'> {
  /** Alternating conversation, oldest first, ending with the learner's newest message. */
  messages: { role: 'user' | 'assistant'; content: string }[];
}

/** SDK client for the learner's own key. The SDK is loaded on first use so it never weighs down pages that do not call it. */
async function client() {
  const apiKey = getApiKey();
  if (!apiKey) throw new AIError(L('No API key configured.', 'Ingen API-nøkkel er lagt inn.'));
  const { default: Anthropic } = await import('@anthropic-ai/sdk');
  return { Anthropic, client: new Anthropic({ apiKey, dangerouslyAllowBrowser: true, maxRetries: 1 }) };
}

/** Maps SDK errors to a friendly, translated AIError. */
function toAIError(e: unknown, Anthropic: typeof AnthropicSDK): AIError {
  if (e instanceof AIError) return e;
  if (e instanceof Anthropic.AuthenticationError) return new AIError(L('The API key was not accepted.', 'API-nøkkelen ble ikke godtatt.'), 401);
  if (e instanceof Anthropic.RateLimitError) return new AIError(L('Too many requests — try again in a moment.', 'For mange forespørsler — prøv igjen om litt.'), 429);
  if (e instanceof Anthropic.APIUserAbortError) {
    const err = new AIError(L('Stopped.', 'Stoppet.'));
    err.name = 'AbortError';
    return err;
  }
  if (e instanceof Anthropic.APIConnectionError) return new AIError(L('Could not reach Claude — check the internet connection.', 'Fikk ikke kontakt med Claude — sjekk internettforbindelsen.'));
  if (e instanceof Anthropic.APIError) {
    const s = e.status ?? 0;
    if (s === 529 || s === 503) return new AIError(L('Claude is busy right now — try again in a minute.', 'Claude er opptatt akkurat nå — prøv igjen om et minutt.'), s);
    if (s === 402 || /credit balance/i.test(e.message)) return new AIError(L('The key has no credits left.', 'Nøkkelen har ikke mer kreditt.'), s);
    if (s === 403) return new AIError(L('The key is not allowed to use this model.', 'Nøkkelen har ikke tilgang til denne modellen.'), s);
    if (s >= 500) return new AIError(L('Claude had a problem — try again soon.', 'Claude fikk et problem — prøv igjen snart.'), s);
    const detail = (e.error as { error?: { message?: string } } | undefined)?.error?.message;
    return new AIError(detail ?? e.message, s);
  }
  return new AIError((e as Error)?.message ?? String(e));
}

export interface JsonOptions<S extends ZodType> {
  model: string;
  system: string;
  prompt: string;
  /** The reply must match this schema (structured outputs); it is validated before it is returned. */
  schema: S;
  maxTokens?: number;
  effort?: 'low' | 'medium' | 'high';
  signal?: AbortSignal;
}

/** One request whose reply is JSON matching `schema` (Claude structured outputs). */
export async function askJson<S extends ZodType>(o: JsonOptions<S>): Promise<zInfer<S>> {
  const { Anthropic, client: c } = await client();
  const { betaZodOutputFormat } = await import('@anthropic-ai/sdk/helpers/beta/zod');
  try {
    const message = await c.beta.messages.parse(
      {
        model: o.model,
        max_tokens: o.maxTokens ?? 16000,
        system: o.system,
        messages: [{ role: 'user', content: o.prompt }],
        output_config: { format: betaZodOutputFormat(o.schema), ...(o.effort ? { effort: o.effort } : {}) },
        ...(supportsFallback(o.model) ? { betas: ['server-side-fallback-2026-07-01'], fallbacks: 'default' as const } : {}),
      },
      { signal: o.signal },
    );
    if (message.stop_reason === 'refusal') throw new AIError(L('The model declined this request.', 'Modellen avslo forespørselen.'));
    if (message.stop_reason === 'max_tokens') throw new AIError(L('The AI answer was cut off. Try again.', 'KI-svaret ble kuttet av. Prøv igjen.'));
    if (message.parsed_output == null) throw new AIError(L('The AI answer could not be read.', 'KI-svaret kunne ikke leses.'));
    return message.parsed_output as zInfer<S>;
  } catch (e) {
    throw toAIError(e, Anthropic);
  }
}

/** Stream a multi-turn conversation. Resolves with the full reply text. */
export async function chat(o: ChatOptions): Promise<string> {
  const { Anthropic, client: c } = await client();
  try {
    const messages: AnthropicSDK.Beta.BetaMessageParam[] = o.messages.map((m) => ({ role: m.role, content: m.content }));
    const stream = c.beta.messages.stream(
      {
        model: o.model,
        // Thinking is on by default on current models and counts toward max_tokens.
        max_tokens: o.maxTokens ?? 16000,
        system: o.system,
        messages,
        ...(supportsFallback(o.model) ? { betas: ['server-side-fallback-2026-07-01'], fallbacks: 'default' as const } : {}),
      },
      { signal: o.signal },
    );
    stream.on('text', (delta, snapshot) => o.onText?.(delta, snapshot));
    const message = await stream.finalMessage();
    if (message.stop_reason === 'refusal') {
      throw new AIError(L('The model declined this request. Try rephrasing it.', 'Modellen avslo forespørselen. Prøv å formulere den annerledes.'));
    }
    return message.content.filter((b) => b.type === 'text').map((b) => (b as { text: string }).text).join('');
  } catch (e) {
    throw toAIError(e, Anthropic);
  }
}
